import { DocumentType, FileValidationStatus } from '@/schema'
import {
  AZURE_BLOB_TYPE_HEADER,
  AZURE_BLOCK_BLOB,
  CONTENT_TYPE_HEADER,
  HTTP_STATUS_OK_MAX,
  HTTP_STATUS_OK_MIN,
  HttpMethod,
  UPLOAD_PROGRESS_COMPLETE,
} from '@/utils/constants'

import { useFileUploadStore } from './store'

interface UploadTask {
  fileId: string
  file: File
  uploadUrl: string
  xhr: XMLHttpRequest
  documentId: string
  fileIndex: number
  versionNumber: number
  documentType: string
}

class BackgroundUploadManager {
  private readonly activeUploads = new Map<string, UploadTask>()

  async uploadFile(
    fileId: string,
    file: File,
    uploadUrl: string,
    documentId: string,
    fileIndex: number,
    versionNumber: number,
    documentType: string
  ): Promise<{
    blob_url: string
    document_id: string
    document_type: string
    file_index: number
    version_number: number
    display_name: string
    file_size: number
  } | null> {
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest()
      xhr.open(HttpMethod.PUT, uploadUrl, true)
      xhr.setRequestHeader(AZURE_BLOB_TYPE_HEADER, AZURE_BLOCK_BLOB)
      xhr.setRequestHeader(CONTENT_TYPE_HEADER, file.type)

      const task: UploadTask = {
        fileId,
        file,
        uploadUrl,
        xhr,
        documentId,
        fileIndex,
        versionNumber,
        documentType,
      }
      this.activeUploads.set(fileId, task)

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = (event.loaded / event.total) * 100
          useFileUploadStore.getState().updateFile(fileId, {
            uploadProgress: percentComplete,
          })
        }
      }

      xhr.onload = () => {
        this.activeUploads.delete(fileId)

        if (
          xhr.status >= HTTP_STATUS_OK_MIN &&
          xhr.status < HTTP_STATUS_OK_MAX
        ) {
          useFileUploadStore.getState().updateFile(fileId, {
            uploadProgress: UPLOAD_PROGRESS_COMPLETE,
            validationStatus: FileValidationStatus.UPLOADED,
          })

          resolve({
            blob_url: uploadUrl.split('?st=')[0],
            document_id: documentId,
            document_type: documentType as DocumentType,
            file_index: fileIndex,
            version_number: versionNumber,
            display_name: file.name,
            file_size: file.size,
          })
        } else {
          useFileUploadStore.getState().updateFile(fileId, {
            validationStatus: FileValidationStatus.FAILED,
            errorMessage: `Upload failed with status ${xhr.status}`,
          })
          resolve(null)
        }
      }

      xhr.onerror = () => {
        this.activeUploads.delete(fileId)
        useFileUploadStore.getState().updateFile(fileId, {
          validationStatus: FileValidationStatus.FAILED,
          errorMessage: 'Upload failed',
        })
        resolve(null)
      }

      xhr.onabort = () => {
        this.activeUploads.delete(fileId)
        useFileUploadStore.getState().updateFile(fileId, {
          validationStatus: FileValidationStatus.CANCELLED,
          errorMessage: 'Upload cancelled',
        })
        resolve(null)
      }

      useFileUploadStore.getState().updateFile(fileId, {
        validationStatus: FileValidationStatus.UPLOADING,
      })

      xhr.send(file)
    })
  }

  cancelUpload(fileId: string): void {
    const task = this.activeUploads.get(fileId)
    if (task) {
      try {
        task.xhr.abort()
      } catch {
        // Ignore
      }
      this.activeUploads.delete(fileId)
    }
  }

  cancelAllUploads(): void {
    this.activeUploads.forEach((task) => {
      try {
        task.xhr.abort()
      } catch {
        // Ignore
      }
    })
    this.activeUploads.clear()
  }

  getActiveUploadCount(): number {
    return this.activeUploads.size
  }

  isUploading(fileId: string): boolean {
    return this.activeUploads.has(fileId)
  }
}

export const backgroundUploadManager = new BackgroundUploadManager()
